import { useRouter } from "@tanstack/react-router";
import { Mail, RefreshCw } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { authClient } from "@/auth";
import { AuthLayout } from "@/components/catalyst/AuthLayout";
import { Button } from "@/components/catalyst/Button";
import { Field, Label } from "@/components/catalyst/Fieldset";
import { Heading } from "@/components/catalyst/Heading";
import { Input } from "@/components/catalyst/Input";
import { Switch, SwitchField } from "@/components/catalyst/Switch";
import { Text, TextLink } from "@/components/catalyst/Text";
import { Logo } from "@/components/Logo";

export function TwoFactorVerify() {
	const router = useRouter();
	const { control } = useForm<{ trustDevice: boolean }>({
		defaultValues: { trustDevice: false },
	});
	const [code, setCode] = useState("");
	const [isSending, setIsSending] = useState(false);
	const [isVerifying, setIsVerifying] = useState(false);
	const [codeSent, setCodeSent] = useState(false);
	const [trustDevice, setTrustDevice] = useState(false);

	const handleSendCode = () => {
		authClient.twoFactor.sendOtp({
			fetchOptions: {
				onSuccess: () => {
					setCodeSent(true);
					toast.success("Verification code sent to your email");
				},
				onError: (ctx) => {
					toast.error(ctx.error.message);
				},
				onResponse: () => {
					setIsSending(false);
				},
			},
		});
	};

	const handleVerify = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!code || code.length !== 6) {
			toast.error("Please enter a 6-digit code");
			return;
		}

		setIsVerifying(true);
		authClient.twoFactor.verifyOtp({
			code,
			trustDevice,
			fetchOptions: {
				onSuccess: () => {
					router.navigate({ to: "/dashboard" });
				},
				onError: (ctx) => {
					toast.error(ctx.error.message);
				},
				onResponse: () => {
					setIsVerifying(false);
				},
			},
		});
	};

	return (
		<AuthLayout>
			<div className="flex flex-col gap-6 w-full max-w-md">
				<div className="flex flex-col items-center gap-4">
					<Logo />
					<Heading>Two-Factor Authentication</Heading>
					<Text className="text-center text-base-content/70">
						{codeSent
							? "Enter the 6-digit code we sent to your email"
							: "Click below to receive a verification code via email"}
					</Text>
				</div>

				<form onSubmit={handleVerify} className="flex flex-col gap-4">
					{!codeSent ? (
						<Button
							type="button"
							onClick={handleSendCode}
							disabled={isSending}
							color="dark/white"
							className="w-full"
						>
							{isSending ? (
								<RefreshCw className="size-4 animate-spin" />
							) : (
								<Mail className="size-4" />
							)}
							{isSending ? "Sending..." : "Send Verification Code"}
						</Button>
					) : (
						<>
							<Field>
								<Label>Verification Code</Label>
								<Input
									type="text"
									inputMode="numeric"
									pattern="[0-9]*"
									maxLength={6}
									value={code}
									onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
									placeholder="000000"
									autoFocus
									className="text-center text-2xl tracking-widest font-mono"
								/>
							</Field>

							<Button
								type="submit"
								disabled={isVerifying || code.length !== 6}
								color="dark/white"
							>
								{isVerifying ? "Verifying..." : "Verify"}
							</Button>

							<Button
								type="button"
								onClick={handleSendCode}
								disabled={isSending}
								outline
							>
								{isSending ? "Sending..." : "Resend code"}
							</Button>
						</>
					)}

					<div className="flex items-center justify-between">
						<SwitchField className="gap-4!">
							<Label className="text-xs font-semibold">Trust this device</Label>
							<Controller
								control={control}
								name="trustDevice"
								render={({ field: { onChange } }) => (
									<Switch
										color="sky"
										checked={trustDevice}
										onChange={(value) => {
											onChange(value);
											setTrustDevice(value);
										}}
									/>
								)}
							/>
						</SwitchField>

						<TextLink href="/auth/2fa/backup" className="text-sm">
							Lost email access?
						</TextLink>
					</div>
				</form>
			</div>
		</AuthLayout>
	);
}
